/**
 * Cloud-backed in-memory database.
 *
 * Public API is intentionally identical to the single-file app's `DB` global:
 * `init()`, `table()`, `add()`, `update()`, `updateByUuid()`, `remove()`,
 * `removeByUuid()`, `replaceAll()`, plus a `save()` localStorage mirror.
 *
 * Reads happen synchronously from `_all` (hydrated at init from Supabase).
 * Writes update the cache + localStorage mirror, then push to Supabase.
 */
import { sb, CLOUD_USER } from "./supabase";
import { toast } from "./dom";
import { Prefs } from "./prefs";
import type { Row, TableKey, TableConfigEntry, ChildConfig } from "./types";

const STORAGE_KEY = "enigma_v1";

/** Order matters: matches the legacy localStorage shape and export ordering. */
export const TABLES: TableKey[] = [
  "sealed", "cards", "transactions", "trades", "rips",
  "grading", "sales", "purchases", "expenses", "mileage", "buys",
  "employees", "timeentries",
  // fmvhistory was MISSING here since cloud FMV history shipped — the nightly
  // job wrote rows nobody ever loaded, so drawer FMV charts stayed empty
  // (found 2026-07-24). Needs the paginated loader: >1000 rows.
  "fmvhistory",
];

/* ============================================================
   Display key <-> Postgres column mapping for each table.
   Children describe nested arrays on parent rows (e.g. sale Items).
   ============================================================ */
export const TABLE_CONFIG: Record<TableKey, TableConfigEntry> = {
  sealed: {
    pgTable: "sealed", pkCol: "lot_id", pkKey: "Lot ID",
    cols: {
      "Lot ID": "lot_id", "Game": "game", "Product": "product", "Set": "set_name",
      "Product Type": "product_type", "Inventory Class": "inventory_class", "Qty": "qty",
      "Acq Date": "acq_date", "Acq Type": "acq_type", "Valuation Source": "valuation_source",
      "Unit Basis": "unit_basis", "Total Basis": "total_basis", "Current FMV": "current_fmv",
      "Unrealized G/L": "unrealized_gl", "Source ID": "source_id", "Notes": "notes",
      "Image URL": "image_url",
      // Status mirrors the cards table - "Available" by default, flipped to "Traded"
      // when fully traded out and "Sold" when fully sold, by the trade/sale flows.
      "Status": "status",
      // Link to the TCGCSV-synced product catalog. Populated when the user picks
      // a product from the Sealed Product picker; used by FMV refresh for
      // precise price lookups (no fuzzy name matching).
      "TCG Product ID": "tcgplayer_product_id",
      // Which TCGplayer printing tier prices this lot ("Unopened", …). Empty
      // = follow revalue_inventory()'s default ladder.
      "Price Variant": "price_variant",
      // Manual-FMV lock: when true, the FMV auto-sweep never touches this lot.
      "FMV Locked": "fmv_locked",
      // User photos of the actual items (lot-photos bucket path array).
      "Photos": "photos",
    },
  },
  cards: {
    pgTable: "cards", pkCol: "lot_id", pkKey: "Card Lot ID",
    cols: {
      "Card Lot ID": "lot_id", "Game": "game", "Card Name": "card_name", "Set": "set_name",
      "Card #": "card_number", "Inventory Class": "inventory_class", "Condition/Grade": "condition_grade",
      "Status": "status", "Qty": "qty", "Acq Date": "acq_date", "Acq Type": "acq_type",
      "Valuation Source": "valuation_source", "Unit Basis": "unit_basis", "Total Basis": "total_basis",
      "Current FMV": "current_fmv", "Unrealized G/L": "unrealized_gl", "Source ID": "source_id", "Notes": "notes",
      "Image URL": "image_url",
      // Manual-FMV lock: when true, the FMV auto-sweep never touches this lot.
      "FMV Locked": "fmv_locked",
      // Canonical catalog identity (Phase 1c of pricing overhaul). TCG Product
      // ID keys into catalog_products; the API-native IDs are provenance.
      "TCG Product ID": "tcgplayer_product_id",
      "PokemonTCG ID": "pokemontcg_id",
      "Scryfall ID": "scryfall_id",
      // Which TCGplayer printing tier prices this lot ("Unlimited Holofoil",
      // "1st Edition", …). Empty = follow revalue_inventory()'s default
      // ladder, which deliberately ranks 1st Edition LAST (2026-08-30).
      "Price Variant": "price_variant",
      // User photos of the actual items (lot-photos bucket path array).
      "Photos": "photos",
    },
  },
  transactions: { // app key (legacy); Postgres table is `adjustments`
    pgTable: "adjustments", pkCol: "txn_id", pkKey: "Txn ID",
    cols: {
      "Txn ID": "txn_id", "Date": "date", "Type": "type", "Tax Category": "tax_category",
      "Related Lot ID": "related_lot_id",
      "Description": "description", "Cash In": "cash_in", "Cash Out": "cash_out",
      "Fees": "fees", "Basis Removed": "basis_removed", "Realized Profit": "realized_profit", "Notes": "notes",
      "Inv Effect": "inv_effect",
    },
    children: {
      "Items": { // contributed items - only populated for Owner Contribution / Gift types
        pgTable: "adjustment_items", parentCol: "adjustment_uuid",
        cols: {
          "AsType": "as_type", "Game": "game", "Item": "item_name", "Set": "set_name",
          "Number": "card_number", "Condition": "condition", "ProductType": "product_type",
          "Qty": "qty", "UnitBasis": "unit_basis", "ContribFMV": "contrib_fmv", "Lot ID": "resulting_lot_id",
        },
      },
    },
  },
  rips: {
    pgTable: "rips", pkCol: "rip_id", pkKey: "Rip ID",
    cols: {
      "Rip ID": "rip_id", "Date": "date", "Sealed Lot ID": "sealed_lot_id", "Sealed Product": "sealed_product",
      "Qty Opened": "qty_opened", "Unit Basis": "unit_basis", "Basis Removed": "basis_removed",
      "Pull FMV": "pull_fmv", "EV Gain/Loss": "ev_gain_loss", "Notes": "notes",
      "Inv Effect": "inv_effect", "Alloc Weights": "alloc_weights",
    },
    children: {
      "Bulk Pulls": {
        pgTable: "rip_bulk_pulls", parentCol: "rip_uuid",
        cols: {
          "Class": "bulk_class", "Qty": "qty", "Unit": "unit_value", "Line Total": "line_total",
        },
      },
    },
  },
  expenses: {
    pgTable: "expenses", pkCol: null, pkKey: null,
    cols: {
      "Date": "date", "Category": "category", "Vendor": "vendor", "Amount": "amount",
      "Deductible": "deductible", "Payment Method": "payment_method", "Receipt Link/Ref": "receipt_link",
      "Receipt File": "receipt_file_path", "Receipt File Name": "receipt_file_name",
      "Receipt File Type": "receipt_file_type", "Notes": "notes",
    },
  },
  mileage: {
    pgTable: "mileage", pkCol: null, pkKey: null,
    cols: {
      "Date": "date", "Purpose": "purpose", "Start Odo": "start_odo", "End Odo": "end_odo",
      "Miles": "miles", "IRS Rate": "irs_rate", "Deduction": "deduction",
      "Related Deal/Lot": "related_deal_lot", "Notes": "notes",
    },
  },
  buys: {
    pgTable: "buys", pkCol: null, pkKey: null,
    cols: {
      "Target": "target", "Category": "category", "Market": "market", "Target Buy %": "target_buy_pct",
      "Max Buy Price": "max_buy_price", "Expected Margin": "expected_margin", "Priority": "priority", "Notes": "notes",
    },
  },
  employees: {
    pgTable: "employees", pkCol: "employee_id", pkKey: "Employee ID",
    cols: {
      "Employee ID": "employee_id", "Name": "name", "Role": "role", "Pay Rate": "pay_rate",
      "Status": "status", "Start Date": "start_date", "Notes": "notes",
      "Password Hash": "password_hash", // portal login; token cols stay unmapped on purpose
    },
  },
  timeentries: {
    pgTable: "time_entries", pkCol: null, pkKey: null,
    cols: {
      "Employee ID": "employee_id", "Employee": "employee_name", "Date": "date", "Hours": "hours",
      "Task": "task", "Paid": "paid", "Paid Date": "paid_date", "Expense Ref": "expense_ref",
      "Approved": "approved", "Entered By": "entered_by",
    },
  },
  // ---- Tables with child arrays ----
  purchases: {
    pgTable: "purchases", pkCol: "purchase_id", pkKey: "Purchase ID",
    cols: {
      "Purchase ID": "purchase_id", "Date": "date", "Vendor": "vendor", "Payment Method": "payment_method",
      "Subtotal": "subtotal", "Fees": "fees", "Total Paid": "total_paid", "Notes": "notes",
      "Receipt File": "receipt_file_path", "Receipt File Name": "receipt_file_name",
      "Receipt File Type": "receipt_file_type",
    },
    children: {
      "Items": {
        pgTable: "purchase_items", parentCol: "purchase_uuid",
        cols: {
          "Type": "item_type", "Game": "game", "Item": "item_name", "Set": "set_name",
          "Number": "card_number", "Condition": "condition", "Grade": "grade", "ProductType": "product_type",
          "Qty": "qty", "UnitCost": "unit_cost", "Lot ID": "resulting_lot_id",
        },
      },
    },
  },
  sales: {
    pgTable: "sales", pkCol: "sale_id", pkKey: "Sale ID",
    cols: {
      "Sale ID": "sale_id", "Date": "date", "Platform": "platform", "Buyer": "buyer",
      "Holding Period": "holding_period", "Subtotal": "subtotal", "Fees": "fees", "Shipping": "shipping",
      "COGS": "cogs", "Net Profit": "net_profit", "Notes": "notes",
      "Has Unknown Holding": "has_unknown_holding", "Inv Effect": "inv_effect",
      "Status": "status",
    },
    children: {
      "Items": {
        pgTable: "sale_items", parentCol: "sale_uuid",
        cols: {
          "Lot ID": "lot_id", "Item": "item_name", "Type": "item_type", "Qty": "qty",
          "Sale Price": "sale_price", "COGS": "cogs",
          "Acq Date": "acq_date", "Holding Days": "holding_days",
          "Tax Treatment": "tax_treatment", "Realized Gain": "realized_gain",
        },
      },
    },
  },
  trades: {
    pgTable: "trades", pkCol: "trade_id", pkKey: "Trade ID",
    cols: {
      "Trade ID": "trade_id", "Date": "date", "Outgoing Basis": "outgoing_basis",
      "Outgoing FMV": "outgoing_fmv", "Incoming FMV": "incoming_fmv",
      "Embedded Gain/Loss": "embedded_gain_loss", "Notes": "notes",
      "Realized Gain": "realized_gain", "Inv Effect": "inv_effect", "Cash": "cash",
      "Status": "status",
    },
    children: {
      "Outgoing": {
        pgTable: "trade_outgoing", parentCol: "trade_uuid",
        cols: {
          "Lot ID": "lot_id", "Item": "item_name", "Type": "item_type", "Qty": "qty",
          "Basis": "basis", "FMV": "fmv",
          "Acq Date": "acq_date", "Holding Days": "holding_days",
          "Tax Treatment": "tax_treatment", "Realized Gain": "realized_gain",
          // Which pool a bulk row belongs to — was in-memory only until
          // 2026-07-26 (reopened trades collapsed all bulk to plain "Bulk").
          "BulkClass": "bulk_class",
        },
      },
      "Incoming": {
        pgTable: "trade_incoming", parentCol: "trade_uuid",
        cols: {
          "AsType": "as_type", "Game": "game", "Item": "item_name", "Set": "set_name",
          "Number": "card_number", "Condition": "condition", "ProductType": "product_type",
          "Qty": "qty", "FMV": "fmv", "Image": "image_url", "Lot ID": "resulting_lot_id",
          "BulkClass": "bulk_class",
        },
      },
    },
  },
  grading: {
    pgTable: "grading", pkCol: "submission_id", pkKey: "Submission ID",
    cols: {
      "Submission ID": "submission_id", "Date Sent": "date_sent", "Date Returned": "date_returned",
      "Status": "status", "Service Types": "service_types", "Provider": "provider",
      "Total Shipping": "total_shipping", "Total Service Fees": "total_service_fees",
      "Total Pre-FMV": "total_pre_fmv", "Total Post-FMV": "total_post_fmv", "Notes": "notes",
      "Inv Effect": "inv_effect",
    },
    children: {
      "Cards": {
        pgTable: "grading_cards", parentCol: "grading_uuid",
        cols: {
          "Card Lot ID": "card_lot_id", "Card Name": "card_name", "Set": "set_name", "Number": "card_number",
          "Service Fee": "service_fee", "Pre-FMV": "pre_fmv", "Grade": "grade", "Post-FMV": "post_fmv", "Notes": "notes",
          "Basis Applied": "basis_applied", "Basis Applied Amount": "basis_applied_amount",
        },
      },
    },
  },
  fmvhistory: {
    pgTable: "fmv_history", pkCol: null, pkKey: null,
    cols: {
      "Lot ID": "lot_id", "Kind": "lot_kind", "Recorded At": "recorded_at",
      "FMV": "fmv", "Qty": "qty", "Source": "source",
    },
  },
};

/* ---------- Generic display <-> Postgres mappers ---------- */
export function toDB(cols: Record<string, string>, row: Row): Record<string, any> {
  const out: Record<string, any> = {};
  for (const k in cols) {
    if (row[k] === undefined) continue;
    let v: any = row[k];
    if (v === "") v = null;
    out[cols[k]] = v;
  }
  return out;
}
export function fromDB(cols: Record<string, string>, r: Record<string, any>): Row {
  const out: Row = {};
  for (const k in cols) out[k] = r[cols[k]];
  return out;
}

/* ============================================================
   Cloud-backed DB instance
   ============================================================ */
type AllTables = Partial<Record<TableKey, Row[]>> & { _version?: number };

class DBImpl {
  _all: AllTables = { _version: 2 };
  _uuid: Partial<Record<TableKey, Record<string, string>>> = {};
  _ready = false;

  /** Per-row serialization of cloud writes. Successive writes to the SAME row
   *  (insert→update, edit-reverse's restore→re-apply's decrement, delete→
   *  re-insert with a recycled id) are queued so they commit in the order
   *  issued — preventing out-of-order races that left stale values after a
   *  reload or tripped the unique constraint. Keyed by `${table}:${pk}`. */
  private _writeChains: Map<string, Promise<void>> = new Map();
  /* ---------- offline write queue (Tier 0 #2, 2026-07-26) ----------
     Failed cloud writes used to survive only until reload (toast + cache-only
     row). Now they persist to localStorage and replay: on init (before the
     app renders), when the browser comes back online, on a 60s interval, and
     via the floating "N unsynced" badge. FIFO with stop-on-first-failure so
     dependent writes (lot insert before sale) keep their order. */
  private _queue: Array<{ t: TableKey; op: "insert" | "update" | "delete"; row?: Row; lookupId?: any; id?: any; ts: number; tries: number }> = [];
  private _queueLoaded = false;
  private _replaying = false;

  private _queueLoad(): void {
    if (this._queueLoaded) return;
    this._queueLoaded = true;
    try { this._queue = JSON.parse(localStorage.getItem("enigma_sync_queue_v1") || "[]"); }
    catch { this._queue = []; }
    this._queueBadge();
    window.addEventListener("online", () => { void this.replayQueue(); });
    setInterval(() => { if (this._queue.length) void this.replayQueue(); }, 60_000);
  }

  private _queueSave(): void {
    try { localStorage.setItem("enigma_sync_queue_v1", JSON.stringify(this._queue)); } catch { /* full */ }
    this._queueBadge();
  }

  /** True for errors the database will keep rejecting (constraint/trigger/
   *  validation) — retrying is pointless and would clog the queue. Network
   *  failures have no PG error code. */
  private _isTerminalDbError(e: any): boolean {
    const code = String(e?.code || "");
    return /^\d{5}$/.test(code) || code.startsWith("PGRST");
  }

  /** Route a failed cloud write: terminal DB rejections surface loudly (the
   *  data was refused, not delayed); everything else queues for retry. */
  private _enqueueOrFail(e: any, op: { t: TableKey; op: "insert" | "update" | "delete"; row?: Row; lookupId?: any; id?: any }): void {
    if (this._isTerminalDbError(e)) {
      toast(`Save rejected by the database: ${e?.message || e}`, "err");
      return;
    }
    this._enqueue(op);
  }

  private _enqueue(op: { t: TableKey; op: "insert" | "update" | "delete"; row?: Row; lookupId?: any; id?: any }): void {
    this._queueLoad();
    if (this._queue.length >= 300) {
      toast("Offline queue is full — reconnect soon or changes may be lost", "err");
      return;
    }
    // Snapshot the row (strip volatile _uuid; it may not survive a reload).
    const snap = op.row ? JSON.parse(JSON.stringify({ ...op.row, _uuid: undefined })) : undefined;
    this._queue.push({ ...op, row: snap, ts: Date.now(), tries: 0 });
    this._queueSave();
    toast("Saved locally — will sync when connection returns");
  }

  private _queueBadge(): void {
    let el = document.getElementById("syncQueueBadge");
    const n = this._queue.length;
    if (!n) { el?.remove(); return; }
    if (!el) {
      el = document.createElement("div");
      el.id = "syncQueueBadge";
      el.style.cssText = "position:fixed;bottom:14px;left:14px;z-index:60;background:#b45309;color:#fff;padding:6px 12px;border-radius:9999px;font-size:12px;cursor:pointer;box-shadow:0 2px 8px rgba(0,0,0,.5)";
      el.title = "Changes saved on this device but not yet in the cloud. Click to retry now. Don't clear browser data until this syncs.";
      el.onclick = () => { void this.replayQueue(); };
      document.body.appendChild(el);
    }
    el.textContent = `⚠ ${n} unsynced change${n === 1 ? "" : "s"}`;
  }

  /** Replay queued writes FIFO; stops at the first failure to keep order. */
  async replayQueue(): Promise<void> {
    this._queueLoad();
    if (this._replaying || !this._queue.length || !CLOUD_USER) return;
    this._replaying = true;
    let drained = 0;
    try {
      while (this._queue.length) {
        const q = this._queue[0]!;
        try {
          if (q.op === "delete") {
            const cfg = TABLE_CONFIG[q.t];
            if (cfg?.pkCol && q.id !== undefined) {
              const { error } = await sb.from(cfg.pgTable).delete()
                .eq("user_id", CLOUD_USER.id).eq(cfg.pkCol, q.id);
              if (error) throw error;
            }
          } else if (q.row) {
            await this._cloudWrite(q.t, q.row as Row, q.op, q.lookupId);
          }
          this._queue.shift();
          this._queueSave();
          drained++;
        } catch (e) {
          if (this._isTerminalDbError(e) || q.tries >= 5) {
            // Dead-letter: the database refuses this write (or it has failed
            // repeatedly) — drop it loudly instead of blocking everything
            // behind it forever.
            console.error("[DB] queue op dropped:", q, e);
            toast(`A queued ${q.op} on ${q.t} was rejected and removed: ${(e as any)?.message || e}`, "err");
            this._queue.shift();
            this._queueSave();
            continue;
          }
          q.tries++;
          this._queueSave();
          console.warn("[DB] queue replay stopped:", e);
          break;
        }
      }
    } finally {
      this._replaying = false;
    }
    if (drained && !this._queue.length) toast(`✓ ${drained} offline change${drained === 1 ? "" : "s"} synced`);
  }

  private _chain(key: string, fn: () => Promise<void>): Promise<void> {
    const prev = this._writeChains.get(key) || Promise.resolve();
    const next = prev.then(fn, fn); // run fn after prev settles, success or fail
    this._writeChains.set(key, next);
    next.finally(() => { if (this._writeChains.get(key) === next) this._writeChains.delete(key); });
    return next;
  }

  async init(): Promise<void> {
    this._all = { _version: 2 };
    this._uuid = {};
    for (const t of TABLES) { this._all[t] = []; this._uuid[t] = {}; }

    if (!CLOUD_USER) {
      // Offline / not signed in - still hydrate prefs from localStorage so the UI works.
      await Prefs.load();
      this._ready = true;
      return;
    }

    // Replay any offline-queued writes from a previous session FIRST, so the
    // cloud reflects local intent before we replace the cache from cloud.
    await this.replayQueue();

    try {
      await Promise.all([
        ...TABLES.map(t => this._loadTable(t)),
        Prefs.load(),
      ]);
    } catch (e: any) {
      console.error("[DB] init load failed:", e);
      toast("Cloud load failed: " + (e?.message || e), "err");
    }
    this._save();
    this._ready = true;
  }

  save(): void {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(this._all)); } catch { /* ignore */ }
    const note = document.getElementById("lastSavedNote");
    if (note) note.textContent = "Cloud-synced " + new Date().toLocaleTimeString();
  }

  /** Re-fetch a single table from cloud, replacing the in-memory cache (+
   *  localStorage mirror). Used by forms that need a current snapshot before
   *  reading - notably the trade form's lot search so it can't show stale qty.
   *  No-ops cleanly when offline. */
  async refreshTable(t: TableKey): Promise<void> {
    if (!CLOUD_USER) return;
    try {
      await this._loadTable(t);
      this._save();
    } catch (e: any) {
      console.error(`[DB] refresh ${t}:`, e);
    }
  }
  _save(): void { this.save(); }

  table(t: TableKey): Row[] {
    return (this._all[t] || []) as Row[];
  }

  add(t: TableKey, row: Row): Promise<void> {
    if (!this._all[t]) this._all[t] = [];
    (this._all[t] as Row[]).push(row);
    this._save();
    // FMV history hook: when a new card/sealed lot is added, write its
    // baseline valuation point so the per-lot price-trend chart has a
    // "from" anchor for the very first day. Uses Acq Date if present,
    // otherwise now(). Fire-and-forget. Skips fmvhistory itself to avoid
    // recursion if this method is ever called from inside the hook.
    if ((t === "cards" || t === "sealed") && (Number(row["Current FMV"]) || 0) !== 0) {
      const lotId = t === "cards" ? row["Card Lot ID"] : row["Lot ID"];
      if (lotId) {
        const acqIso = String(row["Acq Date"] || "").trim();
        const recordedAt = acqIso
          ? (acqIso.length === 10 ? acqIso + "T00:00:00.000Z" : acqIso)
          : new Date().toISOString();
        void this.add("fmvhistory", {
          "Lot ID":      String(lotId),
          "Kind":        t === "cards" ? "card" : "sealed",
          "Recorded At": recordedAt,
          "FMV":         Number(row["Current FMV"]) || 0,
          "Qty":         Number(row["Qty"]) || 0,
          "Source":      "baseline (new lot)",
        });
      }
    }
    const cfg = TABLE_CONFIG[t];
    const key = cfg?.pkKey ? `${t}:${row[cfg.pkKey]}` : `${t}:add:${Math.random()}`;
    const p = this._chain(key, () => this._cloudWrite(t, row, "insert"));
    p.catch((e: any) => {
      console.error(`[DB] insert ${t}:`, e);
      this._enqueueOrFail(e, { t, op: "insert", row });
    });
    return p;
  }

  update(t: TableKey, idField: string, id: any, patch: Partial<Row>): Row | null {
    const r = this.table(t).find(x => x[idField] === id);
    if (!r) return null;
    Object.assign(r, patch);
    this._save();
    const cfg = TABLE_CONFIG[t];
    const key = cfg?.pkKey ? `${t}:${id}` : `${t}:upd:${Math.random()}`;
    this._chain(key, () => this._cloudWrite(t, r, "update", id)).catch((e: any) => {
      console.error(`[DB] update ${t}:`, e);
      this._enqueueOrFail(e, { t, op: "update", row: r, lookupId: id });
    });
    return r;
  }

  /** UUID-based update - preferred for keyless tables. Resolves once cloud write completes. */
  async updateByUuid(t: TableKey, uuid: string, newRow: Row): Promise<Row> {
    const arr = this.table(t);
    const i = arr.findIndex(x => x && x._uuid === uuid);
    if (i < 0) throw new Error(`Row not found in ${t}: ${uuid}`);
    const merged: Row = { ...newRow, _uuid: uuid };
    arr[i] = merged;
    this._save();
    if (!CLOUD_USER) return merged;
    const cfg = TABLE_CONFIG[t];
    const dbRow = toDB(cfg.cols, merged);
    dbRow.user_id = CLOUD_USER.id;
    const { error } = await sb.from(cfg.pgTable).update(dbRow).eq("id", uuid);
    if (error) throw error;
    // Sync child arrays (delete-and-reinsert).
    const updChildren = cfg.children;
    if (updChildren) {
      for (const ck of Object.keys(updChildren)) {
        const ccfg: ChildConfig = updChildren[ck];
        await sb.from(ccfg.pgTable).delete().eq(ccfg.parentCol, uuid);
        const items: Row[] = Array.isArray(merged[ck]) ? (merged[ck] as Row[]) : [];
        if (items.length) {
          const childRows = items.map((it, idx) => ({
            ...toDB(ccfg.cols, it),
            user_id: CLOUD_USER!.id,
            [ccfg.parentCol]: uuid,
            position: idx,
          }));
          const { error: cerr } = await sb.from(ccfg.pgTable).insert(childRows);
          if (cerr) throw cerr;
        }
      }
    }
    return merged;
  }

  /** UUID-based delete - preferred for keyless tables. */
  async removeByUuid(t: TableKey, uuid: string): Promise<void> {
    const arr = this.table(t);
    const i = arr.findIndex(x => x && x._uuid === uuid);
    if (i >= 0) arr.splice(i, 1);
    this._save();
    if (!CLOUD_USER) return;
    const cfg = TABLE_CONFIG[t];
    const { error } = await sb.from(cfg.pgTable).delete().eq("id", uuid);
    if (error) throw error;
    if (cfg.pkKey) {
      const map = this._uuid[t];
      if (map) {
        for (const k of Object.keys(map)) {
          if (map[k] === uuid) { delete map[k]; break; }
        }
      }
    }
  }

  /** Legacy index/key-based remove. Kept for compat with views that use human PKs. */
  remove(t: TableKey, idField: string, id: any): void {
    const arr = this.table(t);
    const i = arr.findIndex(x => x[idField] === id);
    if (i < 0) return;
    arr.splice(i, 1);
    this._save();
    const cfg = TABLE_CONFIG[t];
    if (!cfg) return;
    if (cfg.pkKey) {
      // Chain the delete on the same per-row key as inserts/updates so a
      // delete→re-insert (recycled id) can't race.
      this._chain(`${t}:${id}`, async () => {
        const uuid = this._uuid[t]?.[id];
        if (uuid) {
          const { error } = await sb.from(cfg.pgTable).delete().eq("id", uuid);
          if (error) toast(`Cloud delete failed: ${error.message}`, "err");
          else if (this._uuid[t]) delete this._uuid[t]![id];
        } else if (CLOUD_USER) {
          await sb.from(cfg.pgTable).delete().eq("user_id", CLOUD_USER.id).eq(cfg.pkCol!, id);
        }
      }).catch((e: any) => {
        console.error(`[DB] delete ${t}:`, e);
        this._enqueueOrFail(e, { t, op: "delete", id });
      });
    }
  }

  /** Wipe all the user's data and replace with the supplied backup payload. */
  async replaceAll(
    payload: Record<string, any>,
    onProgress?: (s: { label: string; pct: number; detail?: string }) => void,
  ): Promise<void> {
    if (!CLOUD_USER) throw new Error("Not signed in");
    const log = (label: string, pct: number, detail?: string) =>
      onProgress?.({ label, pct: Math.max(0, Math.min(100, pct)), detail: detail || "" });

    // Phase 1: wipe (RLS confines this to the user's rows).
    const keys = Object.keys(TABLE_CONFIG) as TableKey[];
    for (let i = 0; i < keys.length; i++) {
      const cfg = TABLE_CONFIG[keys[i]];
      log(`Clearing ${cfg.pgTable}...`, Math.round((i / keys.length) * 30));
      const { error } = await sb.from(cfg.pgTable).delete().eq("user_id", CLOUD_USER.id);
      if (error) throw error;
    }

    // Reset cache.
    this._all = { _version: 2 };
    for (const t of TABLES) { this._all[t] = []; this._uuid[t] = {}; }

    // Phase 2: insert payload rows (30 -> 100%).
    let totalRows = 0;
    for (const t of keys) totalRows += Array.isArray(payload[t]) ? payload[t].length : 0;
    let done = 0;
    for (const t of keys) {
      const rows: Row[] = Array.isArray(payload[t]) ? (payload[t] as Row[]).slice() : [];
      // Cache the same references we're about to insert so _cloudWrite's `row._uuid` stamp lands here too.
      this._all[t] = rows;
      for (const row of rows) {
        try { await this._cloudWrite(t, row, "insert"); }
        catch (e: any) {
          console.error(`[DB] bulk insert ${t}:`, e);
          throw new Error(`Failed while inserting ${t}: ${e?.message || e}`);
        }
        done++;
        log(`Uploading ${t}...`, 30 + Math.round((done / Math.max(1, totalRows)) * 70), `${done} / ${totalRows} rows`);
      }
    }
    this._save();
    log("Done", 100);
  }

  /* ---------- internal: paginated fetch (PostgREST silently caps un-ranged
     selects at 1000 rows — fmv_history crossed that 2026-07 and FMV charts
     quietly lost data; every load now pages until a short page) ---------- */
  private async _fetchAll(pgTable: string, orderCol: string, ascending: boolean): Promise<{ rows: Record<string, any>[]; error: any }> {
    const PAGE = 1000;
    const rows: Record<string, any>[] = [];
    for (let from = 0; ; from += PAGE) {
      const { data, error } = await sb.from(pgTable).select("*")
        .order(orderCol, { ascending })
        .range(from, from + PAGE - 1);
      if (error) return { rows, error };
      rows.push(...(data || []));
      if (!data || data.length < PAGE) break;
    }
    return { rows, error: null };
  }

  /* ---------- internal: load one table + its children ---------- */
  private async _loadTable(t: TableKey): Promise<void> {
    const cfg = TABLE_CONFIG[t];
    const { rows: data, error } = await this._fetchAll(cfg.pgTable, "created_at", false);
    if (error) { console.error(`[DB] load ${t}:`, error); return; }
    const out: Row[] = [];
    const uuidByRow: string[] = [];
    for (const r of data || []) {
      const display = fromDB(cfg.cols, r);
      display._uuid = r.id;
      if (cfg.pkKey) {
        const map = this._uuid[t] ||= {};
        map[display[cfg.pkKey] as string] = r.id;
      }
      uuidByRow.push(r.id);
      out.push(display);
    }
    const children = cfg.children;
    if (children) {
      for (const ck of Object.keys(children)) {
        const ccfg: ChildConfig = children[ck];
        const { rows: childRows, error: cerr } = await this._fetchAll(ccfg.pgTable, "position", true);
        if (cerr) { console.error(`[DB] load children ${ck}:`, cerr); continue; }
        const byParent: Record<string, Row[]> = {};
        for (const cr of (childRows || []) as Record<string, any>[]) {
          const pid = cr[ccfg.parentCol];
          (byParent[pid] ||= []).push(fromDB(ccfg.cols, cr));
        }
        for (let i = 0; i < out.length; i++) {
          out[i][ck] = byParent[uuidByRow[i]] || [];
        }
      }
    }
    this._all[t] = out;
  }

  /* ---------- internal: cloud write for keyed tables (insert/update) ---------- */
  private async _cloudWrite(t: TableKey, row: Row, op: "insert" | "update", lookupId?: any): Promise<void> {
    const cfg = TABLE_CONFIG[t];
    if (!CLOUD_USER) return;
    const dbRow: Record<string, any> = toDB(cfg.cols, row);
    dbRow.user_id = CLOUD_USER.id;

    let uuid: string | undefined;
    if (op === "insert") {
      const { data, error } = await sb.from(cfg.pgTable).insert(dbRow).select("id").single();
      if (error) throw error;
      uuid = data.id;
      row._uuid = uuid;
      if (cfg.pkKey) {
        const map = this._uuid[t] ||= {};
        map[row[cfg.pkKey] as string] = uuid!;
      }
    } else {
      uuid = cfg.pkKey ? this._uuid[t]?.[lookupId] : undefined;
      if (!uuid && cfg.pkKey) {
        const { data, error } = await sb.from(cfg.pgTable)
          .select("id").eq("user_id", CLOUD_USER.id).eq(cfg.pkCol!, lookupId).maybeSingle();
        if (error) throw error;
        if (!data) { await this._cloudWrite(t, row, "insert"); return; }
        uuid = data.id;
        const map = this._uuid[t] ||= {};
        map[lookupId] = uuid!;
      }
      if (!uuid) throw new Error(`Could not resolve UUID for ${t} row`);
      const { error } = await sb.from(cfg.pgTable).update(dbRow).eq("id", uuid);
      if (error) throw error;
      if (cfg.pkKey && lookupId !== row[cfg.pkKey]) {
        const map = this._uuid[t] ||= {};
        delete map[lookupId];
        map[row[cfg.pkKey] as string] = uuid!;
      }
    }

    // Sync child arrays (delete-and-reinsert).
    const childrenCfg = cfg.children;
    if (childrenCfg && uuid) {
      for (const ck of Object.keys(childrenCfg)) {
        const ccfg: ChildConfig = childrenCfg[ck];
        await sb.from(ccfg.pgTable).delete().eq(ccfg.parentCol, uuid);
        const items: Row[] = Array.isArray(row[ck]) ? row[ck] : [];
        if (items.length) {
          const childRows = items.map((it, idx) => ({
            ...toDB(ccfg.cols, it),
            user_id: CLOUD_USER!.id,
            [ccfg.parentCol]: uuid,
            position: idx,
          }));
          const { error } = await sb.from(ccfg.pgTable).insert(childRows);
          if (error) throw error;
        }
      }
    }
  }
}

export const DB = new DBImpl();

